/**
 * APX WorkCenter — Login Page
 * Design: "Gold Standard" — Premium Editorial Finance
 * Dark teal card with cream/white text for contrast.
 */

import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { trpcClient } from "@/lib/trpc";

const LOGIN_BG = "/manus-storage/apx-hero-bg-real_eb276f34.webp";

// Standard Operations Hub logo — gold on green background
const APX_LOGO =
  "https://d2xsxph8kpxj0f.cloudfront.net/310419663029793542/5GqF8Kn9QcwXve8h8refaY/apx-logo-final_808b0f18.png";

export default function Login() {
  const { login, loginWithGoogle } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Forgot password state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotStatus, setForgotStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [forgotError, setForgotError] = useState("");

  // NOTE: Using trpcClient directly (not useMutation hook) to avoid React Error #185.
  // useMutation creates a new options object on every render → infinite loop in production.

  useEffect(() => {
    document.title = "APX WORKCENTER™";
  }, []);

  useEffect(() => {
    const status = new URLSearchParams(window.location.search).get("google_auth");
    if (status !== "ready") return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const result = await loginWithGoogle();
      if (cancelled) return;
      if (result.success) {
        window.location.replace("/dashboard");
      } else {
        setLoading(false);
        setError(result.error ?? "Google sign-in failed.");
        window.history.replaceState({}, "", "/login");
      }
    })();
    return () => { cancelled = true; };
  }, [loginWithGoogle]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (!result.success) {
      setError(result.error || "Login failed.");
    } else {
      // Use window.location.replace to navigate to dashboard after login.
      // We do NOT use wouter's navigate() here because wouter monkey-patches
      // history.pushState to call forceStoreRerender, which causes React Error #185.
      window.location.replace("/dashboard");
    }
  };

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotStatus("sending");
    setForgotError("");
    try {
      await trpcClient.staffManagement.requestPasswordReset.mutate({
        email: forgotEmail.trim(),
        origin: window.location.origin,
      });
      setForgotStatus("sent");
    } catch {
      setForgotStatus("error");
      setForgotError("Something went wrong. Please try again.");
    }
  };

  // Text colors for dark card background
  const CREAM = "oklch(0.95 0.01 85)";       // near-white cream for headings
  const SOFT_WHITE = "oklch(0.82 0.01 85)";  // slightly dimmer for body text
  const LABEL = "oklch(0.75 0.01 85)";       // labels

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center relative"
      style={{
        backgroundImage: `url(${LOGIN_BG})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Brand green overlay */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(135deg, oklch(0.22 0.06 185 / 0.45) 0%, oklch(0.18 0.05 185 / 0.40) 50%, oklch(0.20 0.06 185 / 0.42) 100%)",
        }}
      />

      {/* Subtle gold shimmer top-right */}
      <div
        className="absolute top-0 right-0 w-96 h-96 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at top right, oklch(0.92 0.005 240 / 0.12) 0%, transparent 65%)",
        }}
      />
      {/* Subtle gold shimmer bottom-left */}
      <div
        className="absolute bottom-0 left-0 w-80 h-80 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at bottom left, oklch(0.92 0.005 240 / 0.10) 0%, transparent 65%)",
        }}
      />

      {/* Card */}
      <div className="relative z-10 w-full max-w-md mx-4 animate-fade-slide-up">

        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-5">
            <img
              src={APX_LOGO}
              alt="APX Logo"
              className="h-24 w-auto"
              style={{ filter: "drop-shadow(0 6px 20px oklch(0.92 0.005 240 / 40%))" }}
            />
          </div>
          <h1
            className="text-4xl font-bold mb-2"
            style={{
              fontFamily: "'Playfair Display', serif",
              color: CREAM,
              textShadow: "0 2px 12px oklch(0 0 0 / 40%)",
            }}
          >
            APX
          </h1>
          <p
            className="text-sm tracking-[0.25em] uppercase font-medium"
            style={{ color: SOFT_WHITE }}
          >
            APX WORKCENTER™
          </p>
        </div>

        {/* Login Form Card */}
        <div
          className="rounded-2xl p-8"
          style={{
            background:
              "linear-gradient(160deg, oklch(0.29 0.06 185 / 0.92) 0%, oklch(0.25 0.06 185 / 0.95) 100%)",
            backdropFilter: "blur(24px)",
            border: "1px solid oklch(1 0 0 / 14%)",
            boxShadow:
              "0 32px 80px oklch(0 0 0 / 45%), 0 0 0 1px oklch(0.92 0.005 240 / 8%), inset 0 1px 0 oklch(1 0 0 / 10%)",
          }}
        >
          {/* Gold top accent line */}
          <div
            className="w-12 h-0.5 rounded-full mb-6"
            style={{
              background:
                "linear-gradient(90deg, oklch(0.82 0.01 240), oklch(0.92 0.005 240 / 0%))",
            }}
          />

          <h2
            className="text-xl font-semibold mb-1"
            style={{
              fontFamily: "'Playfair Display', serif",
              color: CREAM,
            }}
          >
            Sign in to your APX WorkCenter
          </h2>
          <p className="text-sm mb-7" style={{ color: SOFT_WHITE }}>
            Enter your credentials to continue
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label
                className="block text-sm font-semibold uppercase tracking-wider mb-2"
                style={{ color: LABEL }}
              >
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value.toLowerCase().trim())}
                placeholder="you@apxsystem.io"
                required
                autoComplete="username"
                className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200 login-input"
                style={{
                  background: "oklch(0.33 0.05 185 / 0.8)",
                  border: "1px solid oklch(1 0 0 / 14%)",
                  color: CREAM,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "oklch(0.92 0.005 240 / 70%)";
                  e.target.style.boxShadow = "0 0 0 3px oklch(0.92 0.005 240 / 12%)";
                  e.target.style.background = "oklch(0.35 0.05 185 / 0.9)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "oklch(1 0 0 / 14%)";
                  e.target.style.boxShadow = "none";
                  e.target.style.background = "oklch(0.33 0.05 185 / 0.8)";
                }}
              />
            </div>

            {/* Password */}
            <div>
              <label
                className="block text-sm font-semibold uppercase tracking-wider mb-2"
                style={{ color: LABEL }}
              >
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
                className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200 login-input"
                style={{
                  background: "oklch(0.33 0.05 185 / 0.8)",
                  border: "1px solid oklch(1 0 0 / 14%)",
                  color: CREAM,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "oklch(0.92 0.005 240 / 70%)";
                  e.target.style.boxShadow = "0 0 0 3px oklch(0.92 0.005 240 / 12%)";
                  e.target.style.background = "oklch(0.35 0.05 185 / 0.9)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "oklch(1 0 0 / 14%)";
                  e.target.style.boxShadow = "none";
                  e.target.style.background = "oklch(0.33 0.05 185 / 0.8)";
                }}
              />
            </div>

            {/* Error */}
            {error && (
              <div
                className="flex items-center gap-2.5 px-4 py-3 rounded-xl"
                style={{
                  background: "oklch(0.92 0.005 240 / 12%)",
                  border: "1px solid oklch(0.92 0.005 240 / 25%)",
                }}
              >
                <svg
                  className="w-4 h-4 shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  style={{ color: "oklch(0.82 0.01 240)" }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="text-sm" style={{ color: CREAM }}>
                  {error}
                </span>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 rounded-xl font-semibold text-sm tracking-wide transition-all duration-200 mt-1 disabled:opacity-60"
              style={{
                background:
                  "linear-gradient(135deg, oklch(0.82 0.14 75), oklch(0.72 0.14 75))",
                color: "oklch(0.14 0.04 185)",
                boxShadow:
                  "0 4px 20px oklch(0.72 0.14 75 / 35%), inset 0 1px 0 oklch(1 0 0 / 20%)",
                fontFamily: "'DM Sans', sans-serif",
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  (e.target as HTMLButtonElement).style.boxShadow =
                    "0 8px 28px oklch(0.72 0.14 75 / 50%), inset 0 1px 0 oklch(1 0 0 / 20%)";
                  (e.target as HTMLButtonElement).style.transform = "translateY(-1px)";
                }
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLButtonElement).style.boxShadow =
                  "0 4px 20px oklch(0.72 0.14 75 / 35%), inset 0 1px 0 oklch(1 0 0 / 20%)";
                (e.target as HTMLButtonElement).style.transform = "translateY(0)";
              }}
            >
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>

          <div className="relative my-5 flex items-center justify-center">
            <div className="absolute inset-x-0 h-px" style={{ background: "oklch(1 0 0 / 14%)" }} />
            <span className="relative px-3 text-xs uppercase tracking-wider" style={{ background: "oklch(0.27 0.06 185)", color: LABEL }}>or</span>
          </div>
          <button
            type="button"
            disabled={loading}
            onClick={() => window.location.assign(`/api/google/staff-login?origin=${encodeURIComponent(window.location.origin)}`)}
            className="w-full py-3 rounded-xl font-semibold text-sm transition-all disabled:opacity-60"
            style={{ background: "oklch(0.97 0.005 95)", color: "oklch(0.24 0.03 185)", border: "1px solid oklch(0.9 0.01 95 / 65%)" }}
          >
            Continue with Google
          </button>

          {/* Forgot Password link */}
          <div className="text-center mt-4">
            <button
              type="button"
              onClick={() => {
                setShowForgot(true);
                setForgotEmail(email.toLowerCase().trim()); // pre-fill with whatever they typed
                setForgotStatus("idle");
                setForgotError("");
              }}
              className="text-sm transition-colors duration-150"
              style={{ color: "oklch(0.72 0.14 75 / 80%)", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}
            >
              Forgot your password?
            </button>
          </div>

          <p
            className="text-center text-sm mt-4"
            style={{ color: SOFT_WHITE }}
          >
            Contact your administrator for access
          </p>
        </div>

        {/* ─── Forgot Password Modal ─────────────────────────────────── */}
        {showForgot && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{ background: "oklch(0 0 0 / 65%)" }}
            onClick={(e) => { if (e.target === e.currentTarget) setShowForgot(false); }}
          >
            <div
              className="relative w-full max-w-sm mx-4 rounded-2xl p-8"
              style={{
                background: "linear-gradient(160deg, oklch(0.29 0.06 185 / 0.98) 0%, oklch(0.25 0.06 185 / 0.99) 100%)",
                backdropFilter: "blur(24px)",
                border: "1px solid oklch(1 0 0 / 14%)",
                boxShadow: "0 32px 80px oklch(0 0 0 / 55%)",
              }}
            >
              {/* Close button */}
              <button
                onClick={() => setShowForgot(false)}
                className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center rounded-full transition-colors"
                style={{ color: SOFT_WHITE, background: "oklch(1 0 0 / 8%)" }}
                aria-label="Close"
              >
                ✕
              </button>

              <div
                className="w-10 h-0.5 rounded-full mb-5"
                style={{ background: "linear-gradient(90deg, oklch(0.72 0.14 75), oklch(0.72 0.14 75 / 0%))" }}
              />

              <h2 className="text-lg font-semibold mb-1" style={{ fontFamily: "'Playfair Display', serif", color: CREAM }}>
                Reset Password
              </h2>
              <p className="text-sm mb-6" style={{ color: SOFT_WHITE }}>
                Enter your email and we'll send you a reset link.
              </p>

              {forgotStatus === "sent" ? (
                <div
                  className="flex flex-col items-center gap-3 py-4 text-center"
                >
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ background: "oklch(0.72 0.14 75 / 15%)" }}
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: "oklch(0.72 0.14 75)" }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium" style={{ color: CREAM }}>Check your email</p>
                  <p className="text-xs" style={{ color: SOFT_WHITE }}>If an account exists for that email, a reset link has been sent. It expires in 30 minutes.</p>
                  <button
                    onClick={() => setShowForgot(false)}
                    className="mt-2 text-sm underline"
                    style={{ color: "oklch(0.72 0.14 75 / 80%)", background: "none", border: "none", cursor: "pointer" }}
                  >
                    Back to sign in
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgotSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold uppercase tracking-wider mb-2" style={{ color: LABEL }}>
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value.toLowerCase().trim())}
                      placeholder="you@example.com"
                      required
                      autoFocus
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
                      style={{
                        background: "oklch(0.33 0.05 185 / 0.8)",
                        border: "1px solid oklch(1 0 0 / 14%)",
                        color: CREAM,
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "oklch(0.72 0.14 75 / 70%)";
                        e.target.style.boxShadow = "0 0 0 3px oklch(0.72 0.14 75 / 12%)";
                        e.target.style.background = "oklch(0.35 0.05 185 / 0.9)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "oklch(1 0 0 / 14%)";
                        e.target.style.boxShadow = "none";
                        e.target.style.background = "oklch(0.33 0.05 185 / 0.8)";
                      }}
                    />
                  </div>

                  {forgotStatus === "error" && (
                    <p className="text-sm" style={{ color: "oklch(0.72 0.14 75)" }}>{forgotError}</p>
                  )}

                  <button
                    type="submit"
                    disabled={forgotStatus === "sending"}
                    className="w-full py-3 rounded-xl font-semibold text-sm tracking-wide transition-all duration-200 disabled:opacity-60"
                    style={{
                      background: "linear-gradient(135deg, oklch(0.82 0.14 75), oklch(0.72 0.14 75))",
                      color: "oklch(0.14 0.04 185)",
                      boxShadow: "0 4px 20px oklch(0.72 0.14 75 / 35%)",
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  >
                    {forgotStatus === "sending" ? "Sending…" : "Send Reset Link"}
                  </button>
                </form>
              )}
            </div>
          </div>
        )}

        <p
          className="text-center text-sm mt-5"
          style={{ color: SOFT_WHITE }}
        >
          APX · Secure Internal Platform · apxsystem.io
        </p>
        <p className="text-center mt-2" style={{ display: "flex", justifyContent: "center", gap: "16px" }}>
          <a
            href="/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs"
            style={{ color: SOFT_WHITE, opacity: 0.55, textDecoration: "underline" }}
          >
            Privacy Policy
          </a>
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs"
            style={{ color: SOFT_WHITE, opacity: 0.55, textDecoration: "underline" }}
          >
            Terms of Service
          </a>
        </p>
      </div>
    </div>
  );
}
